import { useState } from 'react'
import { Button, Chip, Table } from '@heroui/react'

import { ModuleIntro } from '@/components/layout'
import { EmptyState, ErrorState, LoadingState, Pagination } from '@/components/ui/data-state'
import { ConfirmDialog } from '@/components/ui/form-modal'
import { PlusIcon } from '@/components/ui/icons'

import { useCountActions, useCountList, useInventoryLocations } from '../hooks'
import type { InventoryLocation, StockCount } from '../types'
import { CountFormDialog } from './count-form-dialog'
import { CountSheetDialog } from './count-sheet-dialog'
import {
  countScopeLabels,
  dateTime,
  documentStatusColors,
  documentStatusLabels,
  locationLabel,
} from './inventory-format'

const PAGE_SIZE = 10

type Step = 'submit' | 'approve' | 'cancel'

const stepCopy: Record<Step, { title: string; confirm: string; fallback: string }> = {
  submit: {
    title: 'Submit this count?',
    confirm: 'Submit',
    fallback: 'Could not submit this count.',
  },
  approve: {
    title: 'Approve this count?',
    confirm: 'Approve and post',
    fallback: 'Could not approve this count.',
  },
  cancel: {
    title: 'Cancel this count?',
    confirm: 'Cancel it',
    fallback: 'Could not cancel this count.',
  },
}

const stepBody: Record<Step, string> = {
  submit:
    'Each counted line’s difference is applied as at the moment it was counted, so sales made since are kept. Lines not counted are left alone. Over your approval threshold the count waits for a manager.',
  approve:
    'The count posts: each counted line’s difference is written to stock. This cannot be undone.',
  cancel: 'The count is set aside and never changes stock.',
}

const isOpen = (count: StockCount) => count.status === 'draft' || count.status === 'counting'

/**
 * Stock counts, newest first. A count lists items at one location; the sheet
 * takes what is on the shelf; submitting posts the differences — or, over the
 * business's approval threshold, leaves the count waiting for a manager.
 */
export function CountsPanel() {
  const [page, setPage] = useState(0)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [formSession, setFormSession] = useState(0)
  /** The count whose sheet is open. */
  const [sheetId, setSheetId] = useState<string | null>(null)
  const [pending, setPending] = useState<{ count: StockCount; step: Step; key: string } | null>(
    null,
  )

  const actions = useCountActions()
  const { counts, total, isLoading, isRefreshing, error, retry } = useCountList(page, PAGE_SIZE)
  const { locations } = useInventoryLocations()

  function openForm() {
    setFormSession((session) => session + 1)
    setIsFormOpen(true)
  }

  function openStep(count: StockCount, step: Step) {
    actions[step].reset()
    setPending({ count, step, key: crypto.randomUUID() })
  }

  function confirmStep() {
    if (!pending) return
    actions[pending.step].mutate(
      { id: pending.count.id, idempotencyKey: pending.key },
      { onSuccess: () => setPending(null) },
    )
  }

  return (
    <>
      <ModuleIntro
        description="Count what is on the shelf. Counting changes nothing; submitting applies the differences as at the moment counted."
        action={
          <Button
            variant="primary"
            size="sm"
            className="h-9 rounded-full px-4 font-medium"
            onPress={openForm}
          >
            <PlusIcon width={16} height={16} />
            New count
          </Button>
        }
      />

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState subject="your counts" error={error} onRetry={retry} />
      ) : counts.length === 0 ? (
        <EmptyState
          title="No counts yet."
          description="Count a location to bring its stock back to what is on the shelf."
          action={{ label: 'New count', onPress: openForm }}
        />
      ) : (
        <>
          <div className={isRefreshing ? 'opacity-60 transition-opacity' : undefined}>
            <CountsTable
              counts={counts}
              locations={locations}
              onOpenSheet={(count) => setSheetId(count.id)}
              onStep={openStep}
            />
          </div>

          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}

      <CountFormDialog
        key={formSession}
        isOpen={isFormOpen}
        onOpenChange={setIsFormOpen}
        onCreated={(count) => {
          setIsFormOpen(false)
          setPage(0)
          setSheetId(count.id)
        }}
      />

      <CountSheetDialog
        countId={sheetId}
        onOpenChange={(open) => {
          if (!open) setSheetId(null)
        }}
      />

      <ConfirmDialog
        isOpen={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null)
        }}
        title={pending ? stepCopy[pending.step].title : ''}
        body={pending && <p>{stepBody[pending.step]}</p>}
        confirmLabel={pending ? stepCopy[pending.step].confirm : ''}
        onConfirm={confirmStep}
        isBusy={pending ? actions[pending.step].isPending : false}
        error={pending ? actions[pending.step].error : null}
        errorFallback={pending ? stepCopy[pending.step].fallback : undefined}
      />
    </>
  )
}

function locationName(id: string, locations: readonly InventoryLocation[]): string {
  const location = locations.find((candidate) => candidate.id === id)
  return location ? locationLabel(location) : '—'
}

interface CountsTableProps {
  counts: StockCount[]
  locations: readonly InventoryLocation[]
  onOpenSheet: (count: StockCount) => void
  onStep: (count: StockCount, step: Step) => void
}

function CountsTable({ counts, locations, onOpenSheet, onStep }: CountsTableProps) {
  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content aria-label="Stock counts">
          <Table.Header>
            <Table.Column id="date" isRowHeader>
              Started
            </Table.Column>
            <Table.Column id="location">Location</Table.Column>
            <Table.Column id="scope">Items</Table.Column>
            <Table.Column id="counted">Counted</Table.Column>
            <Table.Column id="status">Status</Table.Column>
            <Table.Column id="actions">
              <span className="sr-only">Actions</span>
            </Table.Column>
          </Table.Header>

          <Table.Body>
            {counts.map((count) => {
              const counted = count.lines.filter((line) => line.state === 'counted').length
              const label = `the count of ${dateTime(count.createdAt)}`

              return (
                <Table.Row key={count.id} id={count.id}>
                  <Table.Cell>
                    <span className="font-medium tabular-nums">{dateTime(count.createdAt)}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-muted">{locationName(count.locationId, locations)}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-muted">
                      {count.isOpening ? 'Opening count' : countScopeLabels[count.scope]}
                      {count.blind && ' · blind'}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="tabular-nums">
                      {counted} of {count.lines.length}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="flex flex-col items-start gap-1">
                      <Chip size="sm" variant="soft" color={documentStatusColors[count.status]}>
                        {documentStatusLabels[count.status]}
                      </Chip>
                      {count.postedAt && (
                        <span className="text-muted text-xs tabular-nums">
                          {dateTime(count.postedAt)}
                          {count.selfApproved && ' · self-approved'}
                        </span>
                      )}
                      {count.status === 'pending_approval' && count.approvalValue !== null && (
                        <span className="text-muted text-xs tabular-nums">
                          Value {count.approvalValue}
                        </span>
                      )}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="flex justify-end gap-2">
                      <RowButton
                        label={isOpen(count) ? 'Count' : 'View'}
                        aria={`${isOpen(count) ? 'Count' : 'View'} ${label}`}
                        onPress={() => onOpenSheet(count)}
                      />
                      {isOpen(count) && (
                        <RowButton
                          label="Submit"
                          aria={`Submit ${label}`}
                          onPress={() => onStep(count, 'submit')}
                        />
                      )}
                      {count.status === 'pending_approval' && (
                        <RowButton
                          label="Approve"
                          aria={`Approve ${label}`}
                          onPress={() => onStep(count, 'approve')}
                        />
                      )}
                      {(isOpen(count) || count.status === 'pending_approval') && (
                        <RowButton
                          label="Cancel"
                          aria={`Cancel ${label}`}
                          onPress={() => onStep(count, 'cancel')}
                        />
                      )}
                    </span>
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}

function RowButton({ label, aria, onPress }: { label: string; aria: string; onPress: () => void }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={aria}
      onPress={onPress}
      className="border-border h-8 rounded-full border px-3 text-xs font-medium"
    >
      {label}
    </Button>
  )
}
